/**
 * coπ and music theory (Gordon 2026-11-02: "what's a diminished chord …
 * show me a diminished chord in F sharp … show me a lydian scale in B flat —
 * it should pull up a keyboard, play several and explain"). Spoken words →
 * { chord | scale, id, root } for the Chords & scales explorer. English,
 * Swedish, Spanish, French; note names as letters (F sharp, B flat, F#, Bb),
 * Swedish (fiss, bess, ess, H) and do-ré-mi (fa sostenido, si bémol).
 * Only a whole phrase counts — "what's the difference between major and
 * minor?" stays a question for the tutor. Pure.
 */

import { CHORD_LIBRARY, SCALE_LIBRARY, foldTheoryWords, rootAt, type LibraryEntry } from './music/theory-library';
import { NOTE_COMMON } from './music/theory';

export interface TheoryAsk { kind: 'chord' | 'scale'; id: string; root: number | null; name: string; /** 0 = root position */ inv?: number }

const LEADS = [
  'can you show me', 'could you show me', 'please show me', 'show me', 'show us', 'show', 'let me see', 'let me hear', 'i want to see', 'i want to hear',
  'play me', 'play', 'what is', 'whats', 'what s', 'what are', 'explain to me', 'explain', 'tell me about', 'how does', 'how do',
  'kan du visa mig', 'visa mig', 'visa', 'spela', 'vad ar', 'vad betyder', 'forklara', 'beratta om', 'hur later',
  'muestrame', 'muestra', 'ensename', 'pon', 'toca', 'que es', 'que son', 'explicame', 'explica', 'como suena',
  'montre moi', 'montre', 'joue moi', 'joue', 'c est quoi', 'qu est ce qu', 'qu est ce que', 'explique moi', 'explique', 'fais moi ecouter',
].map(l => l.split(' ')).sort((a, b) => b.length - a.length);
const PLAY_LEADS = new Set(['play', 'play me', 'spela', 'pon', 'toca', 'joue', 'joue moi']);

const CHORD_WORDS = new Set(['chord', 'chords', 'ackord', 'ackordet', 'ackorden', 'acorde', 'acordes', 'accord', 'accords', 'triad', 'triads', 'treklang', 'triada', 'triade']);
const SCALE_WORDS = new Set(['scale', 'scales', 'skala', 'skalan', 'skalor', 'escala', 'escalas', 'gamme', 'gammes', 'mode', 'modes', 'modus', 'modo', 'modos']);
const NOTE_ARTICLES = new Set(['an', 'the', 'un', 'una', 'une', 'el', 'le']);
const SOFT_NOTES = new Set(['do', 're', 'mi', 'la', 'si', 'sol', 'ut']);
const ROOT_PREP = new Set(['in', 'on', 'from', 'i', 'pa', 'fran', 'en', 'de', 'sur', 'dans', 'starting']);
const FILLER = new Set([
  'a', 'an', 'the', 'some', 'me', 'us', 'please', 'it', 'sound', 'sounds', 'like', 'look', 'looks', 'on', 'piano', 'keyboard', 'the piano',
  'ett', 'en', 'den', 'det', 'tack', 'lite', 'pa', 'pianot',
  'un', 'una', 'unos', 'unas', 'el', 'la', 'los', 'las', 'por', 'favor', 'al',
  'une', 'le', 'les', 'l', 'des', 'du', 'de', 'd', 's', 'il', 'vous', 'plait', 'au',
  'key', 'tonart', 'tonalidad', 'tonalite', 'note', 'root', 'grundton',
]);

// "first inversion", "första omvändningen", "primera inversión", "premier renversement" …
const INVERSIONS: [string, number][] = [
  ...(['root position', 'grundlage', 'grundposition', 'estado fundamental', 'posicion fundamental', 'etat fondamental', 'position fondamentale'].map(p => [p, 0] as [string, number])),
  ...[['first', 'second', 'third', 'fourth', 'fifth'], ['1st', '2nd', '3rd', '4th', '5th']].flatMap(ws => ws.map((w, i) => [`${w} inversion`, i + 1] as [string, number])),
  ...['forsta', 'andra', 'tredje', 'fjarde', 'femte'].flatMap((w, i) => [[`${w} omvandningen`, i + 1], [`${w} omvandning`, i + 1]] as [string, number][]),
  ...['primera', 'segunda', 'tercera', 'cuarta', 'quinta'].map((w, i) => [`${w} inversion`, i + 1] as [string, number]),
  ...[['premier', 1], ['deuxieme', 2], ['second', 2], ['troisieme', 3], ['quatrieme', 4], ['cinquieme', 5]].map(([w, n]) => [`${w} renversement`, n as number] as [string, number]),
].map(([p, n]) => [foldTheoryWords(p), n] as [string, number]).sort((a, b) => b[0].split(' ').length - a[0].split(' ').length);
const ORDINAL = ['', '1st', '2nd', '3rd', '4th', '5th'];

interface Hit { entry: LibraryEntry<string>; kind: 'chord' | 'scale'; at: number; len: number }

// Names are matched WITHOUT "chord"/"scale" ("diminished chord" → "diminished"):
// those words only tell a chord from a scale. A name that shrinks to one plain
// word ("power", "sixth", "nine") needs the word said with it.
const FORMS: { entry: LibraryEntry<string>; kind: 'chord' | 'scale'; words: string[]; needsWord: boolean }[] = [];
for (const [lib, kind] of [[CHORD_LIBRARY, 'chord'], [SCALE_LIBRARY, 'scale']] as const) {
  for (const e of lib as LibraryEntry<string>[]) {
    const seen = new Set<string>();
    for (const f of Object.values(e.spoken).flat()) {
      const all = foldTheoryWords(f).split(' ');
      // A bare "blues" is the genre — the scale needs "blues scale".
      if (e.id === 'blues' && all.join(' ') === 'blues') continue;
      const words = all.filter(w => !CHORD_WORDS.has(w) && !SCALE_WORDS.has(w));
      if (!words.length || seen.has(words.join(' '))) continue;
      seen.add(words.join(' '));
      FORMS.push({ entry: e, kind, words, needsWord: words.length < all.length && words.length === 1 && !/[a-z]{6,}/.test(words[0]) || e.id === 'blues' });
    }
  }
}

function findType(t: string[], hasWord: boolean): Hit[] {
  const hits: Hit[] = [];
  for (const f of FORMS) {
    if (f.needsWord && !hasWord) continue;
    for (let i = 0; i + f.words.length <= t.length; i++) {
      if (f.words.every((w, j) => t[i + j] === w)) hits.push({ entry: f.entry, kind: f.kind, at: i, len: f.words.length });
    }
  }
  return hits.sort((a, b) => b.len - a.len || a.at - b.at);
}

/** Swedish writes chord names as one word ("mollseptimackord"): split them the way the names are listed. */
const SV_PREFIX: Record<string, string[]> = {
  moll: ['moll'], dur: ['dur'], mollseptim: ['m7'], durseptim: ['maj7'], storseptim: ['maj7'], dominantseptim: ['7'], septim: ['7'],
  forminskat: ['forminskat'], halvforminskat: ['halvforminskat'], forstarkt: ['forstarkt'],
};
const splitSwedish = (words: string[]) => words.flatMap(w => {
  const m = /^([a-z]+?)(ackord|ackordet|ackorden|skala|skalan)$/.exec(w);
  return m && SV_PREFIX[m[1]] ? [...SV_PREFIX[m[1]], m[2]] : [w];
});

export function matchTheory(spoken: string): TheoryAsk | null {
  let t = splitSwedish(foldTheoryWords(spoken).split(' ').filter(Boolean));
  if (!t.length) return null;
  let lead: string[] = [];
  for (const l of LEADS) if (l.every((w, i) => t[i] === w)) { lead = l; break; }
  t = t.slice(lead.length);
  const hasChordWord = t.some(w => CHORD_WORDS.has(w));
  const hasScaleWord = t.some(w => SCALE_WORDS.has(w));
  const hits = findType(t, hasChordWord || hasScaleWord);
  if (!hits.length) return null;
  const used = new Array(t.length).fill(false);
  // Longest name wins ("harmonic minor" over "minor"); the same words can be a
  // chord or a scale ("major", "dur", "mayor") — the word "chord"/"scale" decides.
  const top = hits.filter(h => h.len === hits[0].len && h.at === hits[0].at);
  let hit = top.find(h => (hasScaleWord ? h.kind === 'scale' : hasChordWord ? h.kind === 'chord' : false)) ?? top[0];
  if (hasScaleWord && !hasChordWord && hit.kind === 'chord') hit = top.find(h => h.kind === 'scale') ?? hit;
  for (let i = hit.at; i < hit.at + hit.len; i++) used[i] = true;

  // The root: just before the name ("F sharp diminished", "re menor"), after a
  // preposition ("in F sharp", "en si bémol", "de fa"), or just after the name.
  // "a" is the English article unless it follows "in"/"an"/"the" or has ♯/♭;
  // "do", "re", "la", "si" … are notes before the name or after a preposition.
  let root: number | null = null;
  const noteAt = (i: number, mode: 'before' | 'prep' | 'after') => {
    if (i < 0 || i >= t.length || used[i]) return null;
    const r = rootAt(t, i);
    if (!r) return null;
    if (r.used === 2) return r;
    const w = t[i], prev = t[i - 1];
    if (w === 'a') return mode === 'prep' || (prev && NOTE_ARTICLES.has(prev)) ? r : null;
    if (SOFT_NOTES.has(w) && mode === 'after') return null;
    return r;
  };
  const take = (i: number, r: { pc: number; used: number }) => {
    root = r.pc;
    for (let k = i; k < i + r.used; k++) used[k] = true;
  };
  const two = noteAt(hit.at - 2, 'before');
  const one = noteAt(hit.at - 1, 'before');
  if (two && two.used === 2) take(hit.at - 2, two);
  else if (one && one.used === 1) take(hit.at - 1, one);
  for (let i = 0; i < t.length && root === null; i++) {
    if (!ROOT_PREP.has(t[i]) || used[i]) continue;
    const r = noteAt(i + 1, 'prep');
    if (r) { used[i] = true; take(i + 1, r); }
  }
  if (root === null) { const r = noteAt(hit.at + hit.len, 'after'); if (r) take(hit.at + hit.len, r); }

  // An inversion ("in first inversion", "en primera inversión") — chords only.
  let inv: number | undefined;
  for (const [phrase, n] of INVERSIONS) {
    const w = phrase.split(' ');
    for (let i = 0; i + w.length <= t.length && inv === undefined; i++) {
      if (w.every((x, j) => t[i + j] === x && !used[i + j])) { inv = n; for (let j = 0; j < w.length; j++) used[i + j] = true; }
    }
    if (inv !== undefined) break;
  }

  // Everything else must be small words — otherwise it's a longer question.
  for (let i = 0; i < t.length; i++) {
    if (used[i] || CHORD_WORDS.has(t[i]) || SCALE_WORDS.has(t[i]) || FILLER.has(t[i]) || ROOT_PREP.has(t[i])) continue;
    return null;
  }
  // "major", "minor", "seven" alone need "chord"/"scale" or a root to mean theory;
  // "play …" always needs the word ("play C minor" may be a song).
  if (hit.entry.ambiguous && !hasChordWord && !hasScaleWord && root === null) return null;
  if (PLAY_LEADS.has(lead.join(' ')) && !hasChordWord && !hasScaleWord) return null;

  const rootName = root === null ? '' : `${NOTE_COMMON[root]} `;
  const invName = hit.kind === 'chord' && inv ? `, ${ORDINAL[inv] || `${inv}th`} inversion` : '';
  return { kind: hit.kind, id: hit.entry.id, root, name: `${rootName}${hit.entry.name}${invName}`, ...(hit.kind === 'chord' && inv ? { inv } : {}) };
}

/** The explorer page for an ask; `play` = hear it at once. */
export function theoryHref(a: TheoryAsk, play = true): string {
  const q = new URLSearchParams({ [a.kind]: a.id });
  if (a.root !== null) q.set('root', String(a.root));
  if (a.inv) q.set('inv', String(a.inv));
  if (play) q.set('play', '1');
  return `/academy/chords-and-scales?${q.toString()}`;
}
